import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";

import { formatRateFraction } from "@bw/shared/types";
import type { CommercialDocumentVersion } from "@bw/shared/types/generated";
import { formatDateUtc, parseDecimal } from "@bw/shared/utils";

import { PlatformPage } from "@/components/PlatformPage";
import { StatusBadge } from "@/components/StatusBadge";
import { orgBusinessName } from "@/shared/orgDisplay";

import { getOrg, listContractVersions } from "../api";

function getVersionState(
  effectiveDate: string,
  terminationDate?: string | null,
): string {
  const today = new Date();
  const effective = new Date(`${effectiveDate}T00:00:00Z`);
  if (effective > today) return "Scheduled";

  if (terminationDate) {
    const termination = new Date(`${terminationDate}T00:00:00Z`);
    if (termination <= today) return "Expired";
    return "Grandfathered";
  }

  return "Active";
}

function humanize(value: string): string {
  return value.replace(/_/g, " ");
}

function minimumPayout(value: number | string | null | undefined): string {
  return parseDecimal(value) === null ? "—" : String(value);
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <Box component="div">
      <Typography component="dt" color="text.secondary" variant="caption">
        {label}
      </Typography>
      <Typography
        component="dd"
        variant="body2"
        sx={{ fontWeight: 600, m: 0, mt: 0.5 }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function downloadExactAgreement(document: CommercialDocumentVersion) {
  const blob = new Blob([document.content], {
    type: `${document.media_type};charset=utf-8`,
  });
  const url = URL.createObjectURL(blob);
  const anchor = window.document.createElement("a");
  anchor.href = url;
  const extension = document.media_type === "text/html" ? "html" : "txt";
  anchor.download = `${document.template_key}-${document.template_version}.${extension}`
    .replace(/[^a-zA-Z0-9._-]/g, "-");
  anchor.click();
  URL.revokeObjectURL(url);
}

function ExactAgreementCopy({ document }: { document: CommercialDocumentVersion }) {
  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
      <Stack spacing={1.5} sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          sx={{ alignItems: { sm: "center" }, justifyContent: "space-between" }}
        >
          <Box>
            <Typography component="h3" variant="subtitle1" sx={{ fontWeight: 800 }}>
              Exact agreement copy
            </Typography>
            <Typography color="text.secondary" variant="body2">
              {document.template_key} · {document.template_version} · {document.language}
            </Typography>
          </Box>
          <Button size="small" variant="outlined" onClick={() => downloadExactAgreement(document)}>
            Download copy
          </Button>
        </Stack>
        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
          <Chip size="small" label={document.media_type} />
          <Chip size="small" label={`Approval reference: ${document.approval_reference}`} />
        </Stack>
        <Box sx={{ borderRadius: 2, bgcolor: "action.hover", p: 2, maxHeight: 360, overflow: "auto" }}>
          <Typography component="pre" variant="body2" sx={{ fontFamily: "inherit", m: 0, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
            {document.content}
          </Typography>
        </Box>
        <Typography color="text.secondary" variant="caption" sx={{ fontFamily: "monospace", overflowWrap: "anywhere" }}>
          SHA-256 {document.content_sha256}
        </Typography>
      </Stack>
    </Paper>
  );
}

export function VersionHistoryPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();

  const orgQuery = useQuery({
    queryKey: ["platform", "org", orgId],
    queryFn: () => getOrg(orgId!),
    enabled: Boolean(orgId),
  });

  const versionsQuery = useQuery({
    queryKey: ["platform", "contract-versions", orgId],
    queryFn: () => listContractVersions(orgId!),
    enabled: Boolean(orgId),
  });

  const isLoading = orgQuery.isLoading || versionsQuery.isLoading;
  const isError = orgQuery.isError || versionsQuery.isError;
  const org = orgQuery.data;
  const versions = versionsQuery.data ?? [];

  const actions = (
    <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
      <Button
        type="button"
        size="small"
        variant="outlined"
        onClick={() => navigate(`/organizations/${orgId}`)}
      >
        Back to org
      </Button>
      <Button
        type="button"
        size="small"
        variant="contained"
        onClick={() => navigate(`/organizations/${orgId}/contract-terms`)}
      >
        Publish New Version
      </Button>
    </Stack>
  );

  return (
    <PlatformPage
      title={org ? orgBusinessName(org) : "Contract Versions"}
      actions={actions}
    >
      {isError ? (
        <Alert
          severity="error"
          role="alert"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                void Promise.allSettled([
                  orgQuery.refetch(),
                  versionsQuery.refetch(),
                ]);
              }}
            >
              Retry
            </Button>
          }
        >
          Could not load contract versions. Try again.
        </Alert>
      ) : isLoading ? (
        <Box
          role="status"
          aria-label="Loading contract versions"
          aria-live="polite"
          sx={{ display: "flex", justifyContent: "center", py: 8 }}
        >
          <CircularProgress size={36} />
        </Box>
      ) : versions.length === 0 ? (
        <Box
          role="status"
          aria-live="polite"
          sx={{ py: 6, textAlign: "center" }}
        >
          <Typography color="text.secondary">
            No contract versions published yet.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={2}>
          {versions.map((version) => {
            const state = getVersionState(
              version.effective_date,
              version.termination_date,
            );

            return (
              <Card key={version.version_id} variant="outlined">
                <CardContent>
                  <Stack spacing={2.5}>
                    {(version.service_fee_bindings ?? []).map((binding) => (
                      <Button
                        key={`${binding.fee_policy_version_id}:${binding.selector.purpose}`}
                        size="small"
                        variant="outlined"
                        onClick={() =>
                          navigate(
                            `/contracts/fee-policies/versions/${binding.fee_policy_version_id}`,
                          )
                        }
                      >
                        View exact{" "}
                        {binding.selector.service_key === "classes"
                          ? "classes enrollment"
                          : "service"}{" "}
                        fee terms
                      </Button>
                    ))}
                    <Stack
                      direction="row"
                      spacing={2}
                      sx={{
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box>
                        <Typography color="text.secondary" variant="overline">
                          Version
                        </Typography>
                        <Typography
                          component="h2"
                          variant="h5"
                          sx={{ fontWeight: 700 }}
                        >
                          v{version.version_number}
                        </Typography>
                      </Box>
                      <StatusBadge status={state} />
                    </Stack>

                    <Box
                      component="dl"
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: "repeat(2, minmax(0, 1fr))",
                          lg: "repeat(3, minmax(0, 1fr))",
                        },
                        gap: 2.5,
                        m: 0,
                      }}
                    >
                      <Fact
                        label="Commission"
                        value={formatRateFraction(version.commission_rate)}
                      />
                      {(version.service_fee_bindings ?? []).map((binding) => (
                        <Fact
                          key={`${binding.fee_policy_version_id}:${binding.selector.purpose}`}
                          label="Version-owned fee terms"
                          value={
                            binding.selector.service_key === "classes" &&
                            binding.selector.origin === "player"
                              ? "Classes — Player enrollment"
                              : "Service-specific fee edition"
                          }
                        />
                      ))}
                      <Fact
                        label="Minimum payout"
                        value={minimumPayout(
                          version.payout_schedule?.min_payout_amount,
                        )}
                      />
                      <Fact
                        label="Payout cadence"
                        value={humanize(
                          version.payout_schedule?.frequency ??
                            "Not configured",
                        )}
                      />
                      <Fact
                        label="Payout timezone"
                        value={
                          version.payout_schedule?.timezone ?? "Not configured"
                        }
                      />
                      <Fact
                        label="Effective"
                        value={formatDateUtc(version.effective_date)}
                      />
                      <Fact
                        label="Grandfather / renewal"
                        value={
                          version.termination_date
                            ? formatDateUtc(version.termination_date)
                            : "Open-ended"
                        }
                      />
                      <Fact
                        label="Published"
                        value={formatDateUtc(version.published_at)}
                      />
                      <Fact
                        label="Published by:"
                        value={
                          version.published_by_display_name?.trim() ||
                          "Publisher unavailable"
                        }
                      />
                    </Box>
                    {version.document ? (
                      <ExactAgreementCopy document={version.document} />
                    ) : (
                      <Alert severity="warning">
                        This legacy version has economic terms but no retained exact agreement copy.
                      </Alert>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}
    </PlatformPage>
  );
}
